import React, { useRef } from 'react';

import { Calendar } from 'lucide-react';

import { cn } from './utils';


/**
 * Text input matching the DESIGN.md `input` token:
 * surface-1 bg, hairline-strong border, focus 1px primary + soft glow.
 * Supports optional leading/trailing adornments.
 *
 * For `type="date"` it owns date behavior app-wide: a single trailing calendar
 * button opens the native picker via `showPicker()` (the duplicate native
 * indicator is hidden in global.css), so form fields and inline date inputs
 * behave identically.
 */
const Input = React.forwardRef((props, ref) => {
  const {
    className,
    type = 'text',
    invalid = false,
    leading = null,
    trailing = null,
    ...restProps
  } = props;

  const inputRef = useRef(null);

  const setRef = (node) => {
    inputRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    }
    else if (ref) {
      ref.current = node;
    }
  };

  const openPicker = () => {
    const el = inputRef.current;
    if (el && typeof el.showPicker === 'function') {
      try {
        el.showPicker();
      }
      catch {
        el.focus();
      }
    }
    else {
      el?.focus();
    }
  };

  const isDate = type === 'date';

  const resolvedTrailing = trailing ?? (isDate
    ? (
      <button
        type="button"
        aria-label="Open date picker"
        disabled={restProps.disabled}
        className={cn(
          'flex items-center text-ink-subtle outline-none transition-colors',
          'hover:text-ink focus-visible:text-ink disabled:cursor-not-allowed',
        )}
        onClick={openPicker}
      >
        <Calendar />
      </button>
    )
    : null);

  const adorned = Boolean(leading || resolvedTrailing);

  const input = (
    <input
      ref={setRef}
      type={type}
      className={cn(
        'h-[42px] w-full rounded-md bg-surface-1 px-3 text-[13.5px] text-ink',
        'placeholder:text-ink-subtle outline-none',
        'border border-hairline-strong',
        'focus-visible:border-primary focus-visible:ring-focus',
        'disabled:cursor-not-allowed disabled:opacity-45',
        invalid && 'border-danger focus-visible:border-danger',
        adorned && 'border-0 focus-visible:ring-0 bg-transparent px-0 h-auto',
        !adorned && className,
      )}
      {...restProps}
    />
  );

  if (!adorned) {
    return input;
  }

  return (
    <div
      className={cn(
        'flex h-[42px] w-full items-center gap-2 rounded-md bg-surface-1 px-3',
        'border border-hairline-strong',
        'focus-within:border-primary focus-within:ring-focus',
        invalid && 'border-danger focus-within:border-danger',
        className,
      )}
    >
      {leading
        ? (
          <span className="flex shrink-0 items-center text-ink-subtle [&_svg]:size-4">{leading}</span>
        )
        : null}

      {input}

      {resolvedTrailing
        ? (
          <span className="flex shrink-0 items-center text-ink-subtle [&_svg]:size-4">{resolvedTrailing}</span>
        )
        : null}
    </div>
  );
});

Input.displayName = 'Input';

export { Input };
export default Input;
